import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import type { StoreSearchKeyResponse } from "../../api/storesApi";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import {
  storeHitLabel,
  useStoreSearch,
  type StoreSearchHit,
} from "../../hooks/useStoreSearch";
import { Input } from "../../components/Input";
import { Button } from "../../components/Button";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import {
  SelectEmpty,
  SelectList,
  SelectOptionButton,
  SelectRoot,
} from "../../components/SelectInput";
import { useSelectListBox } from "../../components/useSelectListBox";

const MAX_HITS = 8;

const OptionContent = styled.span`
  display: flex;
  justify-content: space-between;
  gap: ${spacing.x2}px;
`;

const HitMeta = styled.span`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const StatusRow = styled(SelectEmpty)`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
`;

const Selected = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.x2}px;
  padding: ${spacing.x2}px;
  border: 1px solid ${colors.border};
  border-radius: 4px;
`;

type StoreSearchPickerProps = {
  searchConfig: StoreSearchKeyResponse | null;
  value: StoreSearchHit | null;
  onChange: (store: StoreSearchHit | null) => void;
  disabledStoreId?: StoreId;
  disabled?: boolean;
};

export const StoreSearchPicker = ({
  searchConfig,
  value,
  onChange,
  disabledStoreId,
  disabled,
}: StoreSearchPickerProps) => {
  const { t } = useTranslation();
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const storeSearch = useStoreSearch(searchConfig);
  const listBox = useSelectListBox(open, inputRef, listRef);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (
        rootRef.current?.contains(target) ||
        listRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  if (value) {
    return (
      <Selected>
        <div>
          <div>{storeHitLabel(value)}</div>
          <HitMeta>
            {value.orgNumber} · {value.id}
          </HitMeta>
        </div>
        <Button
          type="button"
          $variant="outlined"
          disabled={disabled}
          onClick={() => onChange(null)}
        >
          {t("storeCopy.changeStore")}
        </Button>
      </Selected>
    );
  }

  const query = storeSearch.query.trim();
  const isPending =
    storeSearch.isSearching || storeSearch.searchedQuery?.trim() !== query;
  const hits = isPending ? [] : storeSearch.hits.slice(0, MAX_HITS);
  const selectableHits = hits.filter((hit) => hit.id !== disabledStoreId);
  const highlightedIndex = Math.min(
    activeIndex,
    Math.max(selectableHits.length - 1, 0),
  );
  const optionId = (index: number) => `${listId}-option-${index}`;
  const activeDescendant =
    open && selectableHits[highlightedIndex]
      ? optionId(highlightedIndex)
      : undefined;

  const select = (hit: StoreSearchHit) => {
    setOpen(false);
    onChange(hit);
  };

  const renderStatus = () => {
    if (!searchConfig) {
      return (
        <StatusRow>
          <LoadingSpinner size={14} />
          {t("storeCopy.searchLoading")}
        </StatusRow>
      );
    }
    if (query.length === 0) {
      return <SelectEmpty>{t("storeCopy.searchHint")}</SelectEmpty>;
    }
    if (isPending) {
      return (
        <StatusRow>
          <LoadingSpinner size={14} />
          {t("storeCopy.searching")}
        </StatusRow>
      );
    }
    if (hits.length === 0) {
      return <SelectEmpty>{t("common.noMatches")}</SelectEmpty>;
    }
    return null;
  };

  const status = renderStatus();

  return (
    <SelectRoot ref={rootRef}>
      <Input
        ref={inputRef}
        type="search"
        value={storeSearch.query}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={activeDescendant}
        placeholder={t("storeCopy.searchPlaceholder")}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          storeSearch.updateQuery(e.target.value);
          setActiveIndex(0);
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setOpen(true);
            setActiveIndex((index) =>
              Math.min(index + 1, Math.max(selectableHits.length - 1, 0)),
            );
            return;
          }
          if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex((index) => Math.max(index - 1, 0));
            return;
          }
          if (event.key === "Enter") {
            event.preventDefault();
            const hit = selectableHits[highlightedIndex];
            if (open && hit) {
              select(hit);
            }
            return;
          }
          if (event.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {open &&
        createPortal(
          <SelectList
            ref={listRef}
            id={listId}
            role="listbox"
            $top={listBox.top}
            $left={listBox.left}
            $width={listBox.width}
            $maxHeight={listBox.maxHeight}
          >
            {status ??
              hits.map((hit) => {
                const isDisabled = hit.id === disabledStoreId;
                const selectableIndex = selectableHits.indexOf(hit);
                return (
                  <li key={hit.id} role="presentation">
                    <SelectOptionButton
                      id={isDisabled ? undefined : optionId(selectableIndex)}
                      type="button"
                      role="option"
                      aria-selected={selectableIndex === highlightedIndex}
                      aria-disabled={isDisabled}
                      $active={selectableIndex === highlightedIndex}
                      $selected={false}
                      $disabled={isDisabled}
                      onMouseEnter={() => {
                        if (!isDisabled) setActiveIndex(selectableIndex);
                      }}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        if (!isDisabled) select(hit);
                      }}
                    >
                      <OptionContent>
                        <span>{storeHitLabel(hit)}</span>
                        <HitMeta>
                          {isDisabled
                            ? t("storeCopy.alreadySelected")
                            : hit.orgNumber}
                        </HitMeta>
                      </OptionContent>
                    </SelectOptionButton>
                  </li>
                );
              })}
          </SelectList>,
          document.body,
        )}
    </SelectRoot>
  );
};
